<template>
  <EventPopupModal>
    <template #body>
      <div class="event-popup-schedules">
        <div ref="scrollable" class="event-popup-schedules__sp-scrollable" @scroll="onScroll">
          <div class="event-popup-schedules__title">
            {{ item.event.title }}
          </div>
          <EventPopupCategoryHeader :category="item.event.category" :online="item.event.has_live_schedule" />
          <div class="event-popup-schedules__schedules">
            <div v-for="schedule in item.event.schedules" :key="schedule.schedule_id" class="event-popup-schedules__schedule">
              <OCRadio :value="schedule.schedule_id.toString()" :checked-value.sync="selectedScheduleId">
                <div class="event-popup-schedules__schedule-label">
                  <div v-if="formatScheduleTime(schedule)" class="event-popup-schedules__schedule-time">
                    {{ formatScheduleTime(schedule) }}
                  </div>
                  <div class="event-popup-schedules__schedule-title">{{ schedule.title }}</div>
                  <div
                    v-if="schedule.place != null && schedule.place.length > 0"
                    class="event-popup-schedules__schedule-place"
                  >
                    <span v-if="isOnlineSchedule(schedule)">
                      <InlineSvg
                        :src="require('../../../images/public.svg')"
                        class="event-popup-schedules__place-icon"
                      />
                    </span>
                    <span v-else>
                      <InlineSvg
                        :src="require('../../../images/location_on.svg')"
                        class="event-popup-schedules__place-icon"
                      />
                    </span>
                    {{ schedule.place }}
                  </div>
                </div>
              </OCRadio>
            </div>
          </div>
        </div>
        <div :class="buttonsClass">
          <OCButton label="イベントに申し込む" :disabled="sending" @click="onEntry" />
          <div class="event-popup-schedules__back">
            <a :class="{ disabled: sending }" @click.prevent="onBack">戻る</a>
          </div>
        </div>
      </div>
    </template>
  </EventPopupModal>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, onMounted } from '@vue/composition-api';
import { formatScheduleTime, isOnlineSchedule } from 'utils/event';
import { EventPopupItem } from 'oc2api/popup';
import EventPopupModal from './EventPopupModal.vue';
import EventPopupCategoryHeader from './EventPopupCategoryHeader.vue';
import OCRadio from '../OCRadio.vue';
import OCButton from '../OCButton.vue';
import { debounce } from 'lodash-es';

export type EntryParams = {
  eventId: number;
  scheduleId: number;
};

// スケジュールが多いときにスクロールする部分とボタンの部分の境目に影を表示してスクロール可能であることを示す
// 完全にスクロールしきったときは影を消すという制御のための処理
const useScrollAreaBottomShadow = () => {
  const scrollable = ref<HTMLElement>();
  const buttonsClass = ref<Record<string, boolean>>({ 'event-popup-schedules__buttons': true });
  const hasShadow = () => {
    const element = scrollable.value;
    if (element == null) return false;

    // 参考: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
    const value = Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop);
    return value > 1.0;
  };
  const updateShadow = debounce(() => {
    buttonsClass.value = { ...buttonsClass.value, 'with-shadow': hasShadow() };
  }, 200);
  const onScroll = () => {
    updateShadow();
  };
  onMounted(() => updateShadow());

  return {
    scrollable,
    onScroll,
    buttonsClass,
  };
};

export default defineComponent({
  name: 'EventPopupEntryModal',
  components: {
    EventPopupModal,
    EventPopupCategoryHeader,
    OCRadio,
    OCButton,
  },
  props: {
    item: {
      type: Object as PropType<EventPopupItem>,
      required: true,
    },
    sending: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    entry: (_: EntryParams) => true,
    close: () => true,
  },
  setup(props, { emit }) {
    const selectedScheduleId = ref<string>(props.item.event.schedules[0]?.schedule_id.toString());
    const onEntry = () => {
      const scheduleId = parseInt(selectedScheduleId.value);
      emit('entry', { eventId: props.item.event.id, scheduleId });
    };
    const onBack = () => {
      if (props.sending) return;
      emit('close');
    };

    return {
      selectedScheduleId,
      isOnlineSchedule,
      formatScheduleTime,
      onEntry,
      onBack,
      ...useScrollAreaBottomShadow(),
    };
  },
});
</script>

<style lang="scss" scoped>
@import 'stylesheets/mixins';
@import 'stylesheets/variables';

.event-popup-schedules {
  padding: 56px 40px 32px;

  @media screen and (max-width: 768px) {
    padding: 0px 16px 0;
  }
}

.event-popup-schedules__sp-scrollable {
  @media screen and (max-width: 768px) {
    padding-top: 40px;
    overflow-y: auto;
    // ボタンの領域分を含めて画面高の一定割合を超えないようにする
    // 100vhギリギリを攻めるとアドレスバーで隠れてしまうので注意
    max-height: calc(85vh - 145px);
  }
}

.event-popup-schedules__title {
  font-weight: 600;
  font-size: 22px;
  line-height: 33px;
  color: $oc-black;
}

.event-popup-schedules__schedules {
  margin-top: 32px;

  @media screen and (max-width: 768px) {
    margin-top: 40px;
  }
}

.event-popup-schedules__buttons {
  margin-top: 32px;

  @media screen and (max-width: 768px) {
    margin: 0 -16px 0;
    padding: 32px 16px 32px;
    &.with-shadow {
      box-shadow: 0px 0px 6px rgb(51 51 51 / 30%);
    }
  }
}

.event-popup-schedules__back {
  margin-top: 12px;
  text-align: center;
  a {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: $oc-gray80;
    cursor: pointer;

    &:disabled,
    &:disabled:hover {
      color: $oc-gray10;
    }
  }
}

.event-popup-schedules__schedule {
  & + .event-popup-schedules__schedule {
    margin-top: 16px;
  }
}

.event-popup-schedules__schedule-label {
  margin-left: 16px;
}

.event-popup-schedules__schedule-time {
  font-weight: 700;
  font-size: 14px;
  line-height: 172%;
  letter-spacing: 0.02em;
  color: $oc-black;
}

.event-popup-schedules__schedule-title {
  font-weight: 500;
  font-size: 10px;
  line-height: 150%;
  letter-spacing: 0.02em;
  color: $oc-black;
  .event-popup-schedules__schedule-time + & {
    margin-top: 2px;
  }
}

.event-popup-schedules__schedule-place {
  margin-top: 2px;
  font-weight: 500;
  font-size: 10px;
  line-height: 150%;
  letter-spacing: 0.02em;
  color: $oc-black;
}

.event-popup-schedules__place-icon {
  height: 10px;
}
</style>
